import React, { ReactElement, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import CHElement from "../../model/CHElement";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import ReportButton from "../common/ReportButton";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import Loading from "../common/Loading";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import ButtonType from "../common/button/ButtonType";
import E3dList from "../e3ds/E3dList";
import AnnexDataList from "../annexdata/AnnexDataList";
import ImgUtil from "../../utils/ImgUtil";
import { useAuth } from "../../contexts/AuthContext";
import EntityStats from "../../model/EntityStats";
import ChemiNovaEntityType from "../../model/enums/ChemiNovaEntityType";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import { Tooltip } from "react-tooltip";
import CHElementScale from "../../model/enums/CHElementScale";
import PagedData from "../../model/PagedData";
import Site from "../../model/Site";
import FormDropDown from "../common/FormDropDown";
import Collection from "../../model/Collection";
import MyList from "../../model/MyList";
import GroupingType from "../../model/enums/GroupingType";

function ChElementDetails() {
  // read the ID from the url
  const { id } = useParams();

  // reference to the AuthManager
  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<CHElement>(CHElement.EmptyInstance());
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // flag to display/hide Add to Collection
  const [addToSiteVisible, setAddToSiteVisible] = useState(false);
  // flag to display/hide Add to Collection
  const [addToMyListVisible, setAddToMyListVisible] = useState(false);
  // flag to display/hide Add to Collection
  const [addToCollectionVisible, setAddToCollectionVisible] = useState(false);
  // store the candidate collection until it is confirmed
  const [candidateGroupID, setCandidateGroupID] = useState(-1);
  // indicate if a refresh is necessary
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  // the list of groupings
  const [allSites, setAllSites] = useState<Site[]>([]);
  const [allCollections, setAllCollections] = useState<Collection[]>([]);
  const [allMyLists, setAllMyLists] = useState<MyList[]>([]);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let chElement: CHElement | undefined = undefined;
        if (id) {
          chElement = await apiClient?.get<CHElement>({
            url: APIEndPoints.CHELEMENTS.ONE(parseInt(id)),
          });
          setData(chElement);
        }
        // get the list of groupings (sites / collections / private lists)
        const auxSites = await apiClient?.get<PagedData<Site[]>>({
          url: APIEndPoints.SITES.ALL,
        });
        const auxCollections = await apiClient?.get<PagedData<Collection[]>>({
          url: APIEndPoints.COLLECTIONS.ALL,
        });
        const auxLists = await apiClient?.get<PagedData<MyList[]>>({
          url: APIEndPoints.MY_LISTS.ALL,
        });

        setAllSites(auxSites ? auxSites.items : []);
        setAllCollections(auxCollections ? auxCollections.items : []);
        setAllMyLists(auxLists ? auxLists.items : []);
      } catch (error: any) {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Something went wrong. Try again later!",
          });
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id, refreshNeeded]);

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.CHELEMENTS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "CH Asset deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.CHELEMENTS }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the CH Asset",
          });
        }
      });
  };

  const removeAssetFromGrouping = async (
    groupId: number,
    groupingType: GroupingType,
  ) => {
    // identify the collection in the cached information
    const groupingList =
      groupingType === GroupingType.SITE
        ? allSites
        : groupingType === GroupingType.COLLECTION
          ? allCollections
          : allMyLists;
    // identify the API for this grouping
    const groupingAPIEndpoint =
      groupingType === GroupingType.SITE
        ? APIEndPoints.SITES
        : groupingType === GroupingType.COLLECTION
          ? APIEndPoints.COLLECTIONS
          : APIEndPoints.MY_LISTS;

    const auxGroup = groupingList.filter((c) => c.id === groupId);

    if (!auxGroup || !Array.isArray(auxGroup) || auxGroup.length < 1) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message:
          "Cannot access records. Please refresh the page and try again.",
      });
    } else {
      const group = auxGroup[0];
      group.id_chelements = [];
      if (group.chelements) {
        group.id_chelements = group
          .chelements!.filter((e) => e.id !== data.id)
          .map((e) => e.id!);
      }

      apiClient
        ?.put({
          url: groupingAPIEndpoint.ONE(groupId),
          body: group,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Asset removed successfuly.",
          });
          setRefreshNeeded(!refreshNeeded);
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            alertsManager.showAlertMessage({
              type: AlertMessageType.ERROR,
              message: "Unable to remove asset.",
            });
          }
        });
    }
  };

  const addAssetToGrouping = async (groupingType: GroupingType) => {
    // identify the collection in the cached information
    const groupingList =
      groupingType === GroupingType.SITE
        ? allSites
        : groupingType === GroupingType.COLLECTION
          ? allCollections
          : allMyLists;
    // identify the API for this grouping
    const groupingAPIEndpoint =
      groupingType === GroupingType.SITE
        ? APIEndPoints.SITES
        : groupingType === GroupingType.COLLECTION
          ? APIEndPoints.COLLECTIONS
          : APIEndPoints.MY_LISTS;
    // identify the collection in the cached information
    const auxGroup = groupingList.filter((g) => g.id === candidateGroupID);
    if (!auxGroup || !Array.isArray(auxGroup) || !(auxGroup.length === 1)) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message:
          "Cannot access records. Please refresh the page and try again.",
      });
    } else {
      const group = auxGroup[0];
      group.id_chelements = [];
      if (group.chelements) {
        group.id_chelements = group.chelements.map((e) => e.id!);
      }
      group.id_chelements.push(data.id!);

      apiClient
        ?.put({
          url: groupingAPIEndpoint.ONE(candidateGroupID),
          body: group,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Asset added successfully.",
          });
          setRefreshNeeded(!refreshNeeded);
          setCandidateGroupID(-1);
          if (groupingType === GroupingType.COLLECTION) {
            setAddToCollectionVisible(false);
          } else if (groupingType === GroupingType.MY_LIST) {
            setAddToMyListVisible(false);
          } else {
            setAddToSiteVisible(false);
          }
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            alertsManager.showAlertMessage({
              type: AlertMessageType.ERROR,
              message: "Unable to add this asset.",
            });
          }
        });
    }
  };

  const candidateGroupChanged = (fieldId: string, collectionId: string) => {
    setCandidateGroupID(parseInt(collectionId));
  };

  let materialsDisplay: ReactElement[] | undefined = undefined;
  if (data.materials) {
    materialsDisplay = data.materials?.map((material) => {
      return (
        <li>
          {material.name}
          {material.description ? ": " + material.description : ""}
        </li>
      );
    });
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() || authManager.isAdmin());

  const canAddData =
    !EmbeddingToolInfo.isChemiTool() &&
    (authManager.isAdmin() ||
      authManager.isSpecialist() ||
      authManager.isBoardMember());

  const includingSites: Site[] = allSites.filter((r) => {
    return r.chelements!.filter((e) => e.id === parseInt(id!)).length > 0;
  });
  const includingCollections: Collection[] = allCollections.filter((r) => {
    return r.chelements!.filter((e) => e.id === parseInt(id!)).length > 0;
  });
  const includingMyLists: MyList[] = allMyLists.filter((r) => {
    return r.chelements!.filter((e) => e.id === parseInt(id!)).length > 0;
  });

  const availableSitesOptions = allSites
    .filter(
      (r) =>
        includingSites.filter((s) => s.id === r.id).length === 0 &&
        r.id_user === authManager.getUserID(),
    )
    .map((c) => {
      return { label: c.name, value: c.id! };
    });
  availableSitesOptions.splice(0, 0, {
    label: "-- please select site --",
    value: -1,
  });

  const availableCollectionsOptions = allCollections
    .filter(
      (r) =>
        includingCollections.filter((c) => c.id === r.id).length === 0 &&
        r.id_user === authManager.getUserID(),
    )
    .map((c) => {
      return { label: c.name, value: c.id! };
    });
  availableCollectionsOptions.splice(0, 0, {
    label: "-- please select collection --",
    value: -1,
  });

  const availableMyListsOptions = allMyLists
    .filter(
      (r) =>
        includingMyLists.filter((l) => l.id === r.id).length === 0 &&
        r.id_user === authManager.getUserID(),
    )
    .map((c) => {
      return { label: c.name, value: c.id! };
    });
  availableMyListsOptions.splice(0, 0, {
    label: "-- please select desired list --",
    value: -1,
  });

  const isMac = /Mac/i.test(navigator.platform);
  const isWindows = /Win/i.test(navigator.platform);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt="CH Element icon"
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo className="span-3">
              {AppIcons.USER}
              created by: <span>{data.username}</span>
            </ButtonInfo>
            <ButtonInfo className="span-3">
              {AppIcons.GUID} GUID: <span>{data.GUID}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.E3D}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.E3DMODEL,
                )}
              </span>{" "}
              instances
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.ANNOTATIONS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ANNOTATION,
                )}
              </span>{" "}
              annotations
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.DOCUMENTS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ANNEX_DATA,
                )}
              </span>{" "}
              documents
            </ButtonInfo>
          </div>

          {!EmbeddingToolInfo.isChemiTool() && (
            <div className="flex items-start mt-5 gap-4">
              {canEdit && (
                <Button
                  label="Delete CH Element"
                  purpose={ButtonAction.DELETE}
                  onClick={() => {
                    alertsManager.showDialogWindow({
                      type: DialogWindowType.CONFIRM,
                      message:
                        "Are you sure you want to delete this CH Element?",
                      cancelLabel: "Cancel",
                      confirmLabel: "Yes, delete CH Element",
                      onConfirm: handleDelete,
                    });
                  }}
                />
              )}

              <ReportButton
                entityType="CHElement"
                entityName={data.name}
                entityId={data.id || 0}
              />

              {false && (isMac || isWindows) && (
                <Button
                  label="Open in ChemiAnalysis **"
                  purpose={ButtonAction.USE}
                  onClick={() => {
                    window.location.href = `chemianalysis://token=${authManager.getToken()}&chElementID=${data.id}`;
                  }}
                />
              )}
            </div>
          )}

          {false &&
            (isMac || isWindows) &&
            !EmbeddingToolInfo.isChemiTool() && (
              <div className="flex items-start mt-5">
                **{" "}
                <em>
                  does not work if ChemiAnalysis is not installed on your
                  computer
                </em>
              </div>
            )}

          {EmbeddingToolInfo.isChemiAnalysis() && (
            <div className="flex items-start mt-5">
              <Button
                label="Open in ChemiAnalysis"
                purpose={ButtonAction.USE}
                onClick={() => {
                  const payload = "unity:chelement_id=" + data.id;
                  if (EmbeddingToolInfo.isToolOnWindows()) {
                    // @ts-ignore
                    uwb.ExecuteJsMethod("JSCallback", payload);
                  } else {
                    window.location.href = payload;
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Cultural Heritage Asset details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.CHELEMENTS,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">{data.description}</p>

        <div className="details-section-content">
          <div className="grid grid-cols-2">
            <table className="details-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {data.scale === CHElementScale.Building
                      ? "Author"
                      : "Creator"}
                  </td>
                  <td>{data.author ?? "Not found"}</td>
                </tr>
                <tr>
                  <td>Scale</td>
                  <td>{data.scale ?? "Not found"}</td>
                </tr>
                <tr>
                  <td>Heritage category</td>
                  <td>{data.heritage_category ?? "Not found"}</td>
                </tr>
                <tr>
                  <td>Start year</td>
                  <td>
                    {data.start_year ? Math.abs(data.start_year) : "Not found"}
                    {data.start_year && data.start_year < 0
                      ? ", before Christ"
                      : ""}
                  </td>
                </tr>
                <tr>
                  <td>End year</td>
                  <td>
                    {data.end_year ? Math.abs(data.end_year) : "Not found"}
                    {data.end_year && data.end_year < 0
                      ? ", before Christ"
                      : ""}
                  </td>
                </tr>
                <tr>
                  <td>Approximate dates</td>
                  <td>{data.approximate_date ? "yes" : "no"}</td>
                </tr>
                <tr>
                  <td>Materials</td>
                  <td>{materialsDisplay ?? "Not found"}</td>
                </tr>
              </tbody>
            </table>

            <table className="details-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Country</td>
                  <td>{data.country?.name ?? "Not found"}</td>
                </tr>
                <tr>
                  <td>Province</td>
                  <td>{data.province?.name ?? "Not found"}</td>
                </tr>
                <tr>
                  <td>Municipality</td>
                  <td>{data.municipality?.name ?? "Not found"}</td>
                </tr>
                <tr>
                  <td>Latitude</td>
                  <td>{data.latitude ?? "Not found"}</td>
                </tr>
                <tr>
                  <td>Longitude</td>
                  <td>{data.longitude ?? "Not found"}</td>
                </tr>
                {/* <tr>
                  <td>Cultural Sphere</td>
                  <td>{data.cultural_sphere ?? "Not found"}</td>
                </tr> */}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>
            Documentation
            <span className="tooltip-hint">
              <a data-tooltip-id="my-tooltip" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="my-tooltip">
                You can upload general documents such as archive documents,
                <br />
                old photos, territorial context document, articles, etc. <br />
                <br />
                Please select the most representative documents
                <br /> as the maximum space allowed is 10GB.
              </Tooltip>
            </span>
          </h2>
          {canAddData && (
            <Button
              label="Add document"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.CHELEMENTS,
                    id: data.id,
                    additionalSections: [AppSections.ANNEXDATA],
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
        <div className="details-section-content">
          <AnnexDataList
            id_chelement={data.id}
            id_site={undefined}
            id_e3dmodel={undefined}
          />
        </div>
      </div>

      {/* Sites Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>
            This asset is part of the following sites
            <span className="tooltip-hint">
              <a data-tooltip-id="my-tooltip-sites" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="my-tooltip-sites">
                Cultural Heritage Assets can be part of
                <br />
                one or multiple Cultural Heritage Sites.
                <br />
                <br />
                Visit the Sites section (from main menu) to
                <br />
                explore our assets from this perspective.
              </Tooltip>
            </span>
          </h2>
          {canAddData && (
            <Button
              label="Add this asset to another Site"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() => {
                setCandidateGroupID(-1);
                setAddToMyListVisible(false);
                setAddToCollectionVisible(false);
                setAddToSiteVisible(true);
              }}
            />
          )}
        </div>
        <div className="details-section-content">
          {addToSiteVisible && (
            <div className="details-grid-content p-0 flex flex-row mb-10">
              <div className="details-grid-description grow">
                <FormDropDown
                  options={availableSitesOptions}
                  label=""
                  id="add_to_site"
                  onChange={candidateGroupChanged}
                />
              </div>
              {candidateGroupID !== -1 && (
                <Button
                  type={ButtonType.FILL_ICON_TEXT}
                  purpose={ButtonAction.CREATE}
                  label="Add to Site"
                  onClick={() => addAssetToGrouping(GroupingType.SITE)}
                />
              )}
              <Button
                type={ButtonType.FILL_ICON_TEXT}
                purpose={ButtonAction.CANCEL}
                label="Cancel"
                onClick={() => {
                  setCandidateGroupID(-1);
                  setAddToSiteVisible(false);
                }}
                className="ml-3"
              />
            </div>
          )}
          {includingSites.length === 0 && (
            <div className="text-left">
              This Cultural Heritage Asset has not been included in any Site
              records.
            </div>
          )}
          {includingSites.length > 0 &&
            includingSites.map((collection) => {
              return (
                <div
                  className="details-grid-element"
                  onClick={() => {
                    navigate(
                      SETTINGS.get_path({
                        section: AppSections.SITES,
                        id: collection.id,
                        subsection: AppSubSections.VIEW,
                      }),
                    );
                  }}
                >
                  <div className="details-grid-content p-0 flex flex-row">
                    <div className="details-grid-description grow">
                      {collection.name}
                    </div>

                    {(authManager.getUserID() === collection.id_user ||
                      authManager.isAdmin()) /* only display the delete option if the collection belong to the loogged user */ && (
                      <Button
                        type={ButtonType.ICON}
                        purpose={ButtonAction.DELETE}
                        label=""
                        onClick={() => {
                          alertsManager.showDialogWindow({
                            type: DialogWindowType.CONFIRM,
                            message:
                              "Are you sure you want to remove the asset from this site?",
                            cancelLabel: "Cancel",
                            confirmLabel: "Yes",
                            onConfirm: () =>
                              removeAssetFromGrouping(
                                collection.id!,
                                GroupingType.SITE,
                              ),
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Collections Section */}
      {data.scale === CHElementScale.Object && (
        <div className="details-section">
          <div className="details-section-title">
            <h2>
              This asset is part of the following collections
              <span className="tooltip-hint">
                <a
                  data-tooltip-id="my-tooltip-collections"
                  data-tooltip-place="right"
                >
                  {AppIcons.INFO}
                </a>
                <Tooltip id="my-tooltip-collections">
                  Cultural Heritage Assets can be part of
                  <br />
                  one or multiple Cultural Heritage Collections.
                  <br />
                  <br />
                  Visit the Collections section (from main menu) to
                  <br />
                  explore our assets from this perspective.
                </Tooltip>
              </span>
            </h2>
            {canAddData && (
              <Button
                label="Add this asset to another Collection"
                purpose={ButtonAction.CREATE}
                type={ButtonType.ICON_TEXT}
                onClick={() => {
                  setCandidateGroupID(-1);
                  setAddToMyListVisible(false);
                  setAddToCollectionVisible(true);
                  setAddToSiteVisible(false);
                }}
              />
            )}
          </div>
          <div className="details-section-content">
            {addToCollectionVisible && (
              <div className="details-grid-content p-0 flex flex-row mb-10">
                <div className="details-grid-description grow">
                  <FormDropDown
                    options={availableCollectionsOptions}
                    label=""
                    id="add_to_collection"
                    onChange={candidateGroupChanged}
                  />
                </div>
                {candidateGroupID !== -1 && (
                  <Button
                    type={ButtonType.FILL_ICON_TEXT}
                    purpose={ButtonAction.CREATE}
                    label="Add to collection"
                    onClick={() => addAssetToGrouping(GroupingType.COLLECTION)}
                  />
                )}
                <Button
                  type={ButtonType.FILL_ICON_TEXT}
                  purpose={ButtonAction.CANCEL}
                  label="Cancel"
                  onClick={() => {
                    setCandidateGroupID(-1);
                    setAddToCollectionVisible(false);
                  }}
                  className="ml-3"
                />
              </div>
            )}
            {includingCollections.length === 0 && (
              <div className="text-left">
                This Cultural Heritage Asset has not been included in any
                Collection records.
              </div>
            )}
            {includingCollections.length > 0 &&
              includingCollections.map((collection) => {
                return (
                  <div
                    className="details-grid-element"
                    onClick={() => {
                      navigate(
                        SETTINGS.get_path({
                          section: AppSections.COLLECTIONS,
                          id: collection.id,
                          subsection: AppSubSections.VIEW,
                        }),
                      );
                    }}
                  >
                    <div className="details-grid-content p-0 flex flex-row">
                      <div className="details-grid-description grow">
                        {collection.name}
                      </div>
                      {(authManager.getUserID() === collection.id_user ||
                        authManager.isAdmin()) /* only display the delete option if the collection belong to the loogged user */ && (
                        <Button
                          type={ButtonType.ICON}
                          purpose={ButtonAction.DELETE}
                          label=""
                          onClick={() => {
                            alertsManager.showDialogWindow({
                              type: DialogWindowType.CONFIRM,
                              message:
                                "Are you sure you want to remove the asset from this collection?",
                              cancelLabel: "Cancel",
                              confirmLabel: "Confirm",
                              onConfirm: () =>
                                removeAssetFromGrouping(
                                  collection.id!,
                                  GroupingType.COLLECTION,
                                ),
                            });
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* My lists Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>
            This asset is part of the following lists
            <span className="tooltip-hint">
              <a data-tooltip-id="my-tooltip-lists" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="my-tooltip-lists">
                You can organize the Cultural Heritage Assets from
                <br />
                our database according to your own rules.
                <br />
                <br />
                Manage your private lists and share them with your collaborators
                <br />
                from the top-righ submenu (under your username).
              </Tooltip>
            </span>
          </h2>
          {canAddData && (
            <Button
              label="Add this asset to another List"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() => {
                setCandidateGroupID(-1);
                setAddToMyListVisible(true);
                setAddToCollectionVisible(false);
                setAddToSiteVisible(false);
              }}
            />
          )}
        </div>
        <div className="details-section-content">
          {addToMyListVisible && (
            <div className="details-grid-content p-0 flex flex-row mb-10">
              <div className="details-grid-description grow">
                <FormDropDown
                  options={availableMyListsOptions}
                  label=""
                  id="add_to_my_list"
                  onChange={candidateGroupChanged}
                />
              </div>
              {candidateGroupID !== -1 && (
                <Button
                  type={ButtonType.FILL_ICON_TEXT}
                  purpose={ButtonAction.CREATE}
                  label="Add to My List"
                  onClick={() => addAssetToGrouping(GroupingType.MY_LIST)}
                />
              )}
              <Button
                type={ButtonType.FILL_ICON_TEXT}
                purpose={ButtonAction.CANCEL}
                label="Cancel"
                onClick={() => {
                  setCandidateGroupID(-1);
                  setAddToMyListVisible(false);
                }}
                className="ml-3"
              />
            </div>
          )}
          {includingMyLists.length === 0 && (
            <div className="text-left">
              This Cultural Heritage Asset has not been included in any of your
              private lists.
            </div>
          )}
          {includingMyLists.length > 0 &&
            includingMyLists.map((item) => {
              return (
                <div
                  className="details-grid-element"
                  onClick={() => {
                    navigate(
                      SETTINGS.get_path({
                        section: AppSections.MY_LISTS,
                        id: item.id,
                        subsection: AppSubSections.VIEW,
                      }),
                    );
                  }}
                >
                  <div className="details-grid-content p-0 flex flex-row">
                    <div className="details-grid-description grow">
                      {item.name}
                    </div>
                    {(authManager.getUserID() === item.id_user ||
                      authManager.isAdmin()) /* only display the delete option if the collection belong to the loogged user */ && (
                      <Button
                        type={ButtonType.ICON}
                        purpose={ButtonAction.DELETE}
                        label=""
                        onClick={() => {
                          alertsManager.showDialogWindow({
                            type: DialogWindowType.CONFIRM,
                            message:
                              "Are you sure you want to remove the asset from this list?",
                            cancelLabel: "Cancel",
                            confirmLabel: "Confirm",
                            onConfirm: () =>
                              removeAssetFromGrouping(
                                item.id!,
                                GroupingType.MY_LIST,
                              ),
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* e3Ds Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Condition Assessments</h2>
          {canAddData && (
            <Button
              label="Add Condition Assessment"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.CHELEMENTS,
                    id: data.id,
                    additionalSections: [AppSections.E3DS],
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
        <div className="details-section-content">
          <E3dList />
        </div>
      </div>
    </div>
  );
}

export default ChElementDetails;

// ================================================================================================
// To clear up:
//      1) Allow Admin to add chelement to collection that are not his own? ===> lasam asa
//      2) Allow Admin to view Condition Assessment/Reports of other users on a given CHElement?  ====>. DA
